import React, { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { handleError } from 'reducers/ErrorReducer';
import { makeStyles } from '@material-ui/core/styles';
import Tooltip from 'components/Tooltip';
import InfoIcon from '@material-ui/icons/Info';
import { showReport } from 'reducers/DialogsReducer';
import { searchPlaces } from 'reducers/SearchReducer';
import Fab from '@material-ui/core/Fab';
import { showAddAttachment } from 'reducers/DialogsReducer';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import { showEditPlace } from 'reducers/DialogsReducer';
import XGrid3 from 'components/XGrid3';
import LocalPrintshopRoundedIcon from '@material-ui/icons/LocalPrintshopRounded';
import QuickSearch from './QuickSearch';
import { getFullPermissions } from 'reducers/PermissionsReducer';
import Checkbox2 from 'components/Checkbox2';
import RecordLimitAlert from 'Search/components/RecordLimitAlert';
import MergeTypeIcon from '@material-ui/icons/MergeType';
import PlaceMerge from './PlaceMerge';

const useStyles = makeStyles((theme) => ({
  filters: {
    display: 'flex',
    alignItems: 'baseline',
    marginBottom: theme.spacing(2),
    '& > div': {
      marginRight: theme.spacing(2),
    },
  },
  gridWrap: {
    width: '100%',
    boxSizing: 'border-box',
    height: 600,
  },
  filterWrap: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  filter: {
    width: '100%',
    marginBottom: '8px',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
  },
  panel: {
    '& .MuiDataGridPanelFooter-root': {
      display: 'none',
    },
  },
}));

const colsWithoutDelete = [
  'PlaceID',
  'PlaceName',
  'PlaceType',
  'PlaceORI',
  'PlaceDepartmentID',
  'FullAddressText',
  'Created',
  'CreatedBy',
  'Updated',
  'UpdatedBy',
];

const colsWithDelete = [
  'PlaceID',
  'PlaceName',
  'PlaceType',
  'PlaceORI',
  'PlaceDepartmentID',
  'FullAddressText',
  'Created',
  'CreatedBy',
  'Updated',
  'UpdatedBy',
  'IsDeleted',
  'Deleted',
  'DeletedBy',
  'DeletedIP',
];

let lastSearchTerm = '';

function Place(props) {
  const classes = useStyles();
  const { dataUpdate } = props;
  const permissions = getFullPermissions('globals', 'Places', 'any');
  const mergePermissions = getFullPermissions('globals', 'Merge Places') === true ? true : false;
  const userColumns = permissions.ViewDeleted ? colsWithDelete : colsWithoutDelete;
  const [rows, setRows] = useState([]);
  const [selection, setSelection] = useState(null);
  const [loaded, setLoaded] = useState(true);
  const [placeColumns, setPlaceColumns] = useState(userColumns);
  const [ViewDeleted, setViewDeleted] = useState(false);
  const [printFlag, setPrintFlag] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const limit = 1000;
  const [page, setPage] = useState(0);
  const [showAlert, setShowAlert] = useState(false);
  const [showMerge, setShowMerge] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!dataUpdate || dataUpdate.type !== 'Place') return;
    search();
    // eslint-disable-next-line
  }, [props.dataUpdate]);

  useEffect(() => {
    search();
    // eslint-disable-next-line
  }, [ViewDeleted]);

  useEffect(() => {
    if (rows.length === rowCount) setShowAlert(false);
    else if (rows.length >= limit) setShowAlert(true);
  }, [rows, rowCount]);

  const search = async (text = lastSearchTerm, offset = 0, mergeRows = false) => {
    lastSearchTerm = text;
    if (!text || text.length < 3) return;
    setLoaded(false);
    searchPlaces(text, ViewDeleted, offset, limit)
      .then(async (result) => {
        result.length ? setRowCount(result[0].Count) : setRowCount(0);
        //await getTotalRows(text);
        const nextStartingIndex = rows.length;
        //TRUE means, fetching next 1000 rows
        //so merging new rows with existing
        if (mergeRows)
          setRows((prevState) => [...prevState, ...processData(result, nextStartingIndex)]);
        else {
          setRows(processData(result));
          setPage(0); //resetting page
        }
      })
      .catch(props.handleError)
      .finally(() => setLoaded(true));
  };

  const processData = (data, nextStartingIndex = 0) => {
    return data.map((row, idx) => {
      return {
        ...row,
        id: idx + nextStartingIndex,
      };
    });
  };

  const viewDetails = () => {
    let item = rows.find((row) => row.id === selection);
    if (!item) return;
    props.showReport({ id: 30, data: { ptsPlaceID: item.ptsPlaceID }, title: 'Place Details' });
  };

  const addAttachment = () => {
    const item = rows.find((row) => row.id === selection);
    const { ptsPlaceID } = item;
    props.showAddAttachment({ id: ptsPlaceID, type: 'Place' });
  };

  const add = () => props.showEditPlace(true);

  const edit = () => {
    const item = rows.find((row) => row.id === selection);
    if (!item) return;
    const { ptsPlaceID } = item;
    props.showEditPlace({ ptsPlaceID, ptsAddressID: null });
  };

  const handlePrintClick = () => {
    setPrintFlag(true);
    setTimeout(() => setPrintFlag(false), 1000);
  };

  const onPageChange = () => {
    const offset = (page + 1) * limit;
    const mergingRows = true;
    search(lastSearchTerm, offset, mergingRows);
    setPage(page + 1);
  };

  const handleMerge = () => {
    if (!mergePermissions) {
      dispatch(
        handleError({
          code: 440,
          message: 'You are not permitted to do this action.',
        })
      );
      return;
    }
    setShowMerge(true);
  };

  const isSelectionActive = rows.some(
    (row) => String(row.id) === String(selection) && !row.IsDeleted
  );
  return (
    <div>
      <h5>Places History</h5>
      <div className={classes.filterWrap}>
        <div className={classes.filterWrap}>
          <QuickSearch search={search} loaded={loaded} />
          {permissions.ViewDeleted && (
            <Checkbox2
              label="Show Deleted"
              checked={ViewDeleted}
              onChange={(ev, val) => setViewDeleted(val)}
            />
          )}
        </div>
        <div className={classes.actions}>
          <Tooltip title="Print list">
            <Fab size="small" color="secondary" onClick={handlePrintClick}>
              <LocalPrintshopRoundedIcon />
            </Fab>
          </Tooltip>
          <Tooltip title="Add place">
            <Fab className="ml-2" size="small" color="secondary" onClick={add}>
              <AddIcon />
            </Fab>
          </Tooltip>
          {selection !== null && (
            <>
              <Tooltip title="Edit address">
                <Fab className="ml-2" size="small" color="secondary" onClick={edit}>
                  <EditIcon />
                </Fab>
              </Tooltip>
              <Tooltip title="View details">
                <Fab className="ml-2" size="small" color="secondary" onClick={viewDetails}>
                  <InfoIcon />
                </Fab>
              </Tooltip>
              <Tooltip title="Add attachment">
                <Fab className="ml-2" color="secondary" size="small" onClick={addAttachment}>
                  <AttachFileIcon />
                </Fab>
              </Tooltip>
              {isSelectionActive && (
                <Tooltip title="Merge place">
                  <Fab
                    disabled={mergePermissions === false}
                    className="ml-2"
                    color="secondary"
                    size="small"
                    onClick={handleMerge}>
                    <MergeTypeIcon />
                  </Fab>
                </Tooltip>
              )}
            </>
          )}
        </div>
      </div>
      {showAlert && (
        <RecordLimitAlert
          handleNextPage={onPageChange}
          rowCount={rowCount}
          currentRows={rows.length}
        />
      )}
      {showMerge && (
        <PlaceMerge
          rows={rows}
          targetID={selection}
          close={() => setShowMerge(false)}
          columns={placeColumns}
        />
      )}
      <XGrid3
        name="Places History"
        columns={placeColumns}
        setColumns={setPlaceColumns}
        rows={rows}
        loading={!loaded}
        setSelection={setSelection}
        printFlag={printFlag}
        viewDetails={viewDetails}
        refresh={search}
      />
    </div>
  );
}

const mapStateToProps = (state) => ({
  dataUpdate: state.dataUpdate,
});

export default connect(mapStateToProps, {
  handleError,
  showReport,
  showAddAttachment,
  showEditPlace,
})(Place);
